'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { LayoutDashboard, Radio, BarChart3, ChevronLeft, ChevronRight, Compass, Heart, Bell, ShieldCheck, Upload, TrendingUp, LogOut, Wallet, Zap, Settings, Search, Rocket, BadgeCheck, Activity, Bookmark } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';



interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  activeRoute?: string;
  onCloseMobile?: () => void;
}

const navByRole: Record<string, { label: string; items: { label: string; icon: any; href: string; badge?: string | null; section?: string }[] }[]> = {
  user: [
    {
      label: 'Discover',
      items: [
        { label: 'Feed', icon: Compass, href: '/music-discovery-feed', badge: null },
        { label: 'Campaigns', icon: Rocket, href: '/campaign-discovery', badge: null },
        { label: 'PULSE', icon: Zap, href: '/pulse', badge: 'LIVE' },
        { label: 'Search', icon: Search, href: '/search', badge: null },
        { label: 'Trending', icon: TrendingUp, href: '/music-discovery-feed', badge: null },
      ],
    },
    {
      label: 'My Space',
      items: [
        { label: 'Dashboard', icon: LayoutDashboard, href: '/user-dashboard', section: 'overview', badge: null },
        { label: 'Momentum Wallet', icon: Wallet, href: '/user-dashboard', section: 'wallet', badge: null },
        { label: 'Campaign Tokens', icon: Rocket, href: '/user-dashboard', section: 'tokens', badge: null },
        { label: 'Liked Tracks', icon: Heart, href: '/user-dashboard', section: 'liked', badge: null },
        { label: 'Watchlist', icon: Bookmark, href: '/user-dashboard', section: 'watchlist', badge: null },
        { label: 'Activity', icon: Activity, href: '/user-dashboard', section: 'activity', badge: null },
        { label: 'Notifications', icon: Bell, href: '/user-dashboard', section: 'notifications', badge: null },
        { label: 'Settings', icon: Settings, href: '/settings', badge: null },
      ],
    },
  ],
  artist: [
    {
      label: 'Discover',
      items: [
        { label: 'Feed', icon: Compass, href: '/music-discovery-feed', badge: null },
        { label: 'Campaigns', icon: Rocket, href: '/campaign-discovery', badge: null },
        { label: 'PULSE', icon: Zap, href: '/pulse', badge: 'LIVE' },
        { label: 'Search', icon: Search, href: '/search', badge: null },
      ],
    },
    {
      label: 'Artist',
      items: [
        { label: 'Dashboard', icon: Radio, href: '/artist-dashboard', section: 'overview', badge: null },
        { label: 'Upload Snippet', icon: Upload, href: '/artist-dashboard', section: 'upload', badge: null },
        { label: 'Create Momentum', icon: Rocket, href: '/artist-dashboard', section: 'campaigns', badge: null },
        { label: 'Analytics', icon: BarChart3, href: '/artist-dashboard', section: 'analytics', badge: null },
        { label: 'Verification', icon: BadgeCheck, href: '/artist-dashboard', section: 'verification', badge: null },
        { label: 'Notifications', icon: Bell, href: '/artist-dashboard', section: 'notifications', badge: null },
        { label: 'Settings', icon: Settings, href: '/settings', badge: null },
      ],
    },
  ],
  admin: [
    {
      label: 'Platform',
      items: [
        { label: 'Admin Panel', icon: ShieldCheck, href: '/admin-dashboard', badge: null },
        { label: 'Feed', icon: Compass, href: '/music-discovery-feed', badge: null },
        { label: 'Campaigns', icon: Rocket, href: '/campaign-discovery', badge: null },
        { label: 'PULSE', icon: Zap, href: '/pulse', badge: 'LIVE' },
        { label: 'Search', icon: Search, href: '/search', badge: null },
        { label: 'Settings', icon: Settings, href: '/settings', badge: null },
      ],
    },
  ],
};

export default function Sidebar({ collapsed, onToggleCollapse, activeRoute, onCloseMobile }: SidebarProps) {
  const { user, signOut, supabase } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [unreadCount, setUnreadCount] = useState(0);

  const role: string = (user?.user_metadata?.role as string) || 'user';
  const groups = navByRole[role] || navByRole['user'];

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);

  const currentSection = searchParams?.get('section') || '';

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('is_read', false);
      setUnreadCount(count || 0);
    };
    fetchUnread();

    const channel = supabase
      .channel('sidebar-notif-' + user.id)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${user.id}`,
      }, () => fetchUnread())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const handleSignOut = async () => {
    const userRole = user?.user_metadata?.role;
    await signOut();
    if (userRole === 'admin') {
      router.push('/admin-login');
    } else {
      router.push('/sign-up-login-screen');
    }
  };

  const getItemHref = (item: { href: string; section?: string }) => {
    if (item.section) {
      return `${item.href}?section=${item.section}`;
    }
    return item.href;
  };

  const isItemActive = (item: { href: string; section?: string }) => {
    if (item.href !== pathname) return false;
    if (item.section) {
      return currentSection === item.section;
    }
    // For items without section (like Settings), just match pathname
    return true;
  };

  return (
    <aside
      className="flex flex-col h-full border-r"
      style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b flex-shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}
        style={{ borderColor: 'var(--border)' }}>
        {!collapsed && (
          <Link href="/" className="flex items-center gap-2" onClick={onCloseMobile}>
            <img
              src="/assets/images/logo-removebg-preview_2_-1778277745384.png"
              alt="ECHOMINT"
              style={{ height: 36, width: 'auto', objectFit: 'contain' }}
            />
          </Link>
        )}
        {collapsed && (
          <Link href="/" onClick={onCloseMobile}>
            <img
              src="/assets/images/logo-removebg-preview_2_-1778277745384.png"
              alt="ECHOMINT"
              style={{ height: 32, width: 'auto', objectFit: 'contain' }}
            />
          </Link>
        )}
        <button
          onClick={onToggleCollapse}
          className={`p-1.5 rounded-lg transition-all duration-150 hover:bg-muted ${collapsed ? 'hidden lg:flex' : 'flex'}`}
          style={{ color: 'var(--muted-foreground)' }}
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {groups.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-widest"
                style={{ color: 'var(--muted-foreground)', letterSpacing: '0.1em' }}>
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = isItemActive(item);
              const isNotif = item.label === 'Notifications';
              const href = getItemHref(item);
              return (
                <Link
                  key={`nav-${item.label}`}
                  href={href}
                  onClick={onCloseMobile}
                  title={collapsed ? item.label : undefined}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 text-sm font-medium
                    nav-item-hover transition-all duration-150
                    ${isActive ? 'nav-item-active' : ''}
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  style={{
                    color: isActive ? 'var(--secondary)' : 'var(--muted-foreground)',
                  }}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge && item.badge !== 'LIVE' && (
                        <span className="badge-purple text-xs px-1.5 py-0.5 rounded-full font-semibold">
                          {item.badge}
                        </span>
                      )}
                      {item.badge === 'LIVE' && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                          style={{ background: 'rgba(42,157,143,0.15)', color: 'var(--secondary)' }}>
                          LIVE
                        </span>
                      )}
                      {isNotif && unreadCount > 0 && (
                        <span className="badge-purple text-xs px-1.5 py-0.5 rounded-full font-bold">
                          {unreadCount}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t p-3" style={{ borderColor: 'var(--border)' }}>
        <button
          onClick={handleSignOut}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium nav-item-hover ${collapsed ? 'justify-center' : ''}`}
          style={{ color: 'var(--muted-foreground)' }}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span>Sign Out</span>}
        </button>
        {!collapsed && user && (
          <div className="mt-3 p-3 rounded-lg" style={{ background: 'var(--muted)' }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #2A9D8F, #3BBFB0)' }}>
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate">{displayName}</p>
                <p className="text-xs truncate capitalize" style={{ color: 'var(--muted-foreground)' }}>{role}</p>
              </div>
              <div className="w-2 h-2 rounded-full pulse-dot" style={{ background: 'var(--positive)' }} />
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}